/**
 * Расчёт аварийного маршрута к укрытию/выходу с честным фолбэком (Resilience):
 * основной путь — по пешеходной сети (planEscape), а там, где сеть не доводит,
 * добавляется прямое плечо-пунктир (анти-фейк: не притворяемся, что дорога
 * нарисована). Случаи:
 *  - сеть доводит до узла укрытия, но сама точка укрытия в стороне → финальное
 *    прямое плечо от последнего узла до точки;
 *  - у укрытия нет узла или узел в несвязанной компоненте графа → весь путь
 *    прямым пунктиром от стартовой точки (маршрут никогда не пропадает молча).
 */
import { haversineM, type LonLat } from '@app/geo'
import { type BuiltGraph, type EscapePlan, planEscape, type Venue } from '@app/rvf'
import type { EmergencyFrame } from './frame'
import { MODE_NAMES } from './frame'

/** Порог «сеть дотянута» — как OFF_NETWORK_EPS_M в rvf/route. */
const OFF_NETWORK_EPS_M = 3

export interface EscapeRoute {
  /** Путь по сети; null — сеть не доводит (весь путь прямой). */
  readonly plan: EscapePlan | null
  /** Прямое плечо-пунктир (финальное или весь путь); null — не нужно. */
  readonly direct: readonly LonLat[] | null
}

export const NO_ESCAPE: EscapeRoute = { plan: null, direct: null }

/**
 * Стартовый узел эвакуации.
 *
 * Порядок неслучаен и проверен на живых данных (31.07): на реальной карте
 * якорей может не быть вовсе, и тогда маршрут не строился ВООБЩЕ — человек в
 * аварийном режиме видел горящие помещения и ни одной линии к выходу.
 *
 *  1. Где человек стоит (геолокация) — самое верное.
 *  2. Якорь площадки (пришёл по QR — знаем точку входа).
 *  3. Узел, ближайший к центру карты, — заведомо приблизительно, но линия к
 *     ближайшему выходу всё равно показывает НАПРАВЛЕНИЕ. Панель в этом случае
 *     честно говорит, что не знает, где человек находится.
 */
export function startNodeId(v: Venue, g?: BuiltGraph, from?: LonLat | null): string | null {
  if (from && g) {
    const near = nearestNodeId(g, from)
    if (near) return near
  }
  for (const anchor of v.anchors) {
    if (anchor.node) return anchor.node
  }
  if (g) return nearestNodeId(g, v.venue.center as LonLat)
  return null
}

/** Ближайший узел графа к точке — по прямой; уровень не учитываем: в ЧС
 *  важнее вывести, чем угадать этаж. */
function nearestNodeId(g: BuiltGraph, point: LonLat): string | null {
  let best: string | null = null
  let bestM = Number.POSITIVE_INFINITY
  for (const [id, node] of g.nodes) {
    const d = haversineM(node.point, point)
    if (d < bestM) {
      bestM = d
      best = id
    }
  }
  return best
}

/**
 * Ближайшее укрытие к точке `from` (по прямой) — цель ВНЕШНЕГО плеча, когда
 * наша сеть не доводит: `shelterId` фиксирует выбранное тапом укрытие, иначе
 * берём геометрически ближайшее к пользователю. null — укрытий нет.
 */
export function nearestShelterPoint(
  v: Venue,
  from: LonLat,
  shelterId: string | null,
): LonLat | null {
  const shelters = v.evacuation?.shelters ?? []
  if (shelterId) return shelters.find((s) => s.id === shelterId)?.point ?? null
  let best: LonLat | null = null
  let bestM = Number.POSITIVE_INFINITY
  for (const s of shelters) {
    const d = haversineM(from, s.point)
    if (d < bestM) {
      bestM = d
      best = s.point
    }
  }
  return best
}

/**
 * Целевые узлы из evacuation: exits для EVACUATION (mode 0), shelters для
 * SHELTER (mode 1). DRILL/фолбэк — пусто (UI честно скажет «следуйте
 * указаниям персонала»).
 */
export function targetNodeIds(v: Venue, frame: EmergencyFrame): readonly string[] {
  const evac = v.evacuation
  if (frame.mode === 0) {
    const ids = evac?.exits?.flatMap((e) => (e.node ? [e.node] : [])) ?? []
    if (ids.length) return ids
    // Размеченных эвакуационных выходов нет — а выходы у здания есть всегда.
    // Узел типа entrance это и есть выход наружу: на живой карте (31.07) без
    // этого запасного варианта эвакуация не строила ни одной линии.
    return v.network.nodes.filter((n) => n.kind === 'entrance').map((n) => n.id)
  }
  if (frame.mode === 1) {
    const ids = evac?.shelters?.flatMap((s) => (s.node ? [s.node] : [])) ?? []
    if (ids.length) return ids
  }
  return []
}

/**
 * Подводим к выходу, до которого не доведена сеть: строим маршрут к ближайшим
 * к нему ДОСТИЖИМЫМ узлам, а последний отрезок рисуем пунктиром. Человек видит
 * и путь, и куда с него выйти, вместо пустого экрана.
 */
function bridgeToExit(
  g: BuiltGraph,
  v: Venue,
  targets: readonly string[],
  start: string,
  ctx: ReturnType<typeof buildCtx>,
): EscapeRoute | null {
  const startPoint = g.nodes.get(start)?.point
  if (!startPoint) return null
  const exitPoint = nearestPointOf(targets, v, startPoint)
  if (!exitPoint) return null
  // Кандидаты — дюжина узлов вокруг выхода: planEscape сам выберет из них тот,
  // до которого действительно доходит сеть, и по кратчайшему пути.
  const near = nodesNearestTo(g, exitPoint, 12)
  const plan = near.length ? planEscape(g, start, near, ctx) : null
  if (!plan) return { plan: null, direct: [startPoint, exitPoint] }
  const last = plan.polyline[plan.polyline.length - 1]
  return { plan, direct: last ? [last, exitPoint] : null }
}

/** Точка ближайшего к `from` выхода из списка узлов-целей. */
function nearestPointOf(targets: readonly string[], v: Venue, from: LonLat): LonLat | undefined {
  let best: LonLat | undefined
  let bestM = Number.POSITIVE_INFINITY
  for (const id of targets) {
    const node = v.network.nodes.find((n) => n.id === id)
    if (!node) continue
    const d = haversineM(node.point as LonLat, from)
    if (d < bestM) {
      bestM = d
      best = node.point as LonLat
    }
  }
  return best
}

/** N узлов графа, ближайших к точке (кандидаты «подойти как можно ближе»). */
function nodesNearestTo(g: BuiltGraph, point: LonLat, n: number): string[] {
  return [...g.nodes.entries()]
    .map(([id, node]) => [id, haversineM(node.point, point)] as const)
    .sort((a, b) => a[1] - b[1])
    .slice(0, n)
    .map(([id]) => id)
}

export function buildCtx(frame: EmergencyFrame) {
  return {
    mode: MODE_NAMES[frame.mode],
    blockedNodes: new Set(frame.blocksNodes),
    blockedEdges: new Set(frame.blocksEdges),
    smokeNodes: new Set(frame.smokeNodes),
  }
}

/** Точка цели для финального плеча: выбранное укрытие или укрытие плана. */
function shelterPointFor(
  v: Venue,
  plan: EscapePlan | null,
  picked: { readonly point: LonLat } | null,
): LonLat | undefined {
  if (picked) return picked.point
  if (!plan) return undefined
  return v.evacuation?.shelters?.find((sh) => sh.node === plan.targetNodeId)?.point
}

/**
 * Маршрут эвакуации: сеть + прямые плечи. `shelterId` — перевыбор тапом
 * (null = ближайшее автоматически).
 */
export function buildEscapeRoute(
  g: BuiltGraph,
  v: Venue,
  frame: EmergencyFrame,
  shelterId: string | null,
  from?: LonLat | null,
): EscapeRoute {
  const start = startNodeId(v, g, from)
  if (!start) return NO_ESCAPE
  const picked = shelterId
    ? (v.evacuation?.shelters?.find((sh) => sh.id === shelterId) ?? null)
    : null
  // Выбранное укрытие без узла → сразу прямой пунктир (не подменяем цель другой).
  const targets = picked ? (picked.node ? [picked.node] : []) : targetNodeIds(v, frame)
  const ctx = buildCtx(frame)
  const plan = targets.length ? planEscape(g, start, targets, ctx) : null
  // Выход есть, но сеть до него не доведена — частая беда живых карт (на
  // Ülemiste 31.07 оба узла-выхода вообще ни к чему не подключены). Молчать
  // нельзя: ведём по сети как можно ближе к выходу, дальше — честный пунктир.
  if (!plan && targets.length) {
    const bridged = bridgeToExit(g, v, targets, start, ctx)
    if (bridged) return bridged
  }
  if (plan) return withFinalLeg(plan, shelterPointFor(v, plan, picked))
  // Сеть не доводит (узла нет или он в несвязанной компоненте): прямой пунктир.
  const startPoint = g.nodes.get(start)?.point
  if (picked && startPoint) return { plan: null, direct: [startPoint, picked.point] }
  return NO_ESCAPE
}

/** Последний отрезок до точки цели, если сеть обрывается не доходя до неё. */
function withFinalLeg(plan: EscapePlan, target: LonLat | undefined): EscapeRoute {
  const last = plan.polyline[plan.polyline.length - 1]
  const needsLeg =
    target !== undefined && last !== undefined && haversineM(last, target) > OFF_NETWORK_EPS_M
  return { plan, direct: needsLeg ? [last as LonLat, target as LonLat] : null }
}
